import type { Product, ProductVariant } from '../types';

export function resolveProductMoq(product?: Pick<Product, 'min_order_quantity'> | null): number {
  return Math.max(1, Math.floor(Number(product?.min_order_quantity ?? 1)));
}

export function resolveVariantMoq(
  product?: Pick<Product, 'min_order_quantity'> | null,
  variant?: Pick<ProductVariant, 'min_order_quantity'> | null,
): number {
  const v = variant?.min_order_quantity;
  if (v != null && Number.isFinite(Number(v)) && Number(v) > 0) {
    return Math.max(1, Math.floor(Number(v)));
  }
  return resolveProductMoq(product);
}

export function isMoqMultiple(quantity: unknown, moq: number): boolean {
  const qty = Math.floor(Number(quantity ?? 0));
  const step = Math.max(1, Math.floor(Number(moq ?? 1)));
  if (qty <= 0) return false;
  return qty >= step && qty % step === 0;
}

export function snapToMoqMultiple(quantity: unknown, moq: number): number {
  const step = Math.max(1, Math.floor(Number(moq ?? 1)));
  let qty = Math.max(0, Math.floor(Number(quantity ?? 0)));
  if (qty <= 0) return step;
  if (qty < step) return step;
  const rem = qty % step;
  if (rem === 0) return qty;
  return qty + (step - rem);
}

export function moqStepHint(moq: number, unit?: string | null): string {
  const step = Math.max(1, Math.floor(Number(moq ?? 1)));
  const u = unit ? String(unit) : '件';
  return `${step}${u}起订，须按 ${step} 的倍数采购`;
}

export function validateLineMoqQuantity(
  quantity: unknown,
  moq: number,
  label = '数量',
): string | null {
  const qty = Math.floor(Number(quantity ?? 0));
  const step = Math.max(1, Math.floor(Number(moq ?? 1)));
  if (qty <= 0) return `${label}须大于 0`;
  if (qty < step) return `${label}不能少于起订量 ${step}`;
  if (qty % step !== 0) return `${label}须为起订量 ${step} 的整数倍`;
  return null;
}

export function resolveCartLineMoq(item: {
  product: Pick<Product, 'min_order_quantity' | 'variants' | 'has_variants'> & {
    variant_min_order_quantity?: number | null;
  };
  productVariantId?: number | null;
}): number {
  const vid = item.productVariantId ?? null;
  if (vid != null && item.product.variants?.length) {
    const variant = item.product.variants.find((v) => v.id === vid);
    if (variant) return resolveVariantMoq(item.product, variant);
  }
  const snapMoq = item.product.variant_min_order_quantity;
  if (vid != null && snapMoq != null && Number(snapMoq) > 0) {
    return Math.max(1, Math.floor(Number(snapMoq)));
  }
  return resolveProductMoq(item.product);
}

export function validateMultiVariantMoqTotals(input: {
  lines: Array<{ quantity: number; variant?: Pick<ProductVariant, 'min_order_quantity'> | null }>;
  product: Pick<Product, 'min_order_quantity' | 'unit'>;
}): string | null {
  const active = input.lines.filter((row) => Math.floor(Number(row.quantity ?? 0)) > 0);
  if (!active.length) return '请至少选择一个规格并填写数量';

  for (const row of active) {
    const lineMoq = resolveVariantMoq(input.product, row.variant ?? null);
    const err = validateLineMoqQuantity(row.quantity, lineMoq, '规格数量');
    if (err) return err;
  }
  return null;
}
